import React, { useState } from 'react';
import { Globe, Upload, Wifi, WifiOff, FileText } from 'lucide-react';

type Platform = 'didi' | 'uber' | 'rappi' | 'resumen';

const PLATFORMS: Array<{ id: Platform; label: string; color: string }> = [
  { id: 'didi', label: 'DiDi Food', color: 'bg-orange-500' },
  { id: 'uber', label: 'Uber Eats', color: 'bg-green-600' },
  { id: 'rappi', label: 'Rappi', color: 'bg-red-500' },
  { id: 'resumen', label: 'Resumen', color: 'bg-accent' },
];

const PlatformTab: React.FC<{ platform: typeof PLATFORMS[number] }> = ({ platform }) => {
  const [notes, setNotes] = useState('');

  if (platform.id === 'resumen') {
    return (
      <div className="text-center py-16">
        <Globe size={64} className="mx-auto mb-6 text-accent opacity-40" />
        <h3 className="text-xl font-semibold text-dark-text mb-3">Vista Combinada</h3>
        <p className="text-medium-gray max-w-md mx-auto">
          Aqui se mostrara un resumen de ventas de todas las plataformas una vez que esten conectadas.
        </p>
        <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg mx-auto">
          {PLATFORMS.filter(p => p.id !== 'resumen').map(p => (
            <div key={p.id} className="bg-cream/50 rounded-lg p-4 text-center">
              <div className={`w-3 h-3 rounded-full ${p.color} mx-auto mb-2 opacity-30`} />
              <p className="text-sm font-medium text-medium-gray">{p.label}</p>
              <p className="text-xs text-light-gray mt-1">No conectado</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      <div className="flex items-center justify-between p-4 bg-cream/50 rounded-lg border border-light-gray/20">
        <div className="flex items-center">
          <div className={`w-10 h-10 rounded-lg ${platform.color} flex items-center justify-center mr-4`}>
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h4 className="font-medium text-dark-text">{platform.label}</h4>
            <p className="text-sm text-medium-gray">Estado de conexion</p>
          </div>
        </div>
        <div className="flex items-center text-medium-gray">
          <WifiOff size={18} className="mr-2" />
          <span className="text-sm font-medium">No conectado</span>
        </div>
      </div>

      {/* Proximamente Message */}
      <div className="text-center py-8 border-2 border-dashed border-light-gray/30 rounded-lg">
        <div className={`w-16 h-16 rounded-full ${platform.color} opacity-20 mx-auto mb-4 flex items-center justify-center`}>
          <Globe size={32} className="text-white" />
        </div>
        <h3 className="text-lg font-semibold text-dark-text mb-2">Proximamente</h3>
        <p className="text-sm text-medium-gray max-w-md mx-auto">
          La integracion con {platform.label} esta en desarrollo. Podras sincronizar pedidos, ver metricas y conectar automaticamente con tu inventario.
        </p>
      </div>

      {/* Manual Upload Zone */}
      <div className="bg-white rounded-lg border border-light-gray/20 p-6">
        <h4 className="font-medium text-dark-text mb-4 flex items-center">
          <Upload size={18} className="mr-2 text-accent" />
          Carga Manual de Reportes
        </h4>
        <div className="border-2 border-dashed border-light-gray/30 rounded-lg p-8 text-center hover:border-accent/50 transition-colors cursor-pointer">
          <FileText size={32} className="mx-auto mb-3 text-medium-gray opacity-50" />
          <p className="text-sm text-medium-gray mb-1">
            Arrastra un archivo CSV o Excel aqui
          </p>
          <p className="text-xs text-light-gray">
            O haz clic para seleccionar un archivo
          </p>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-lg border border-light-gray/20 p-6">
        <h4 className="font-medium text-dark-text mb-3">Notas de Configuracion</h4>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anota credenciales API, pasos de configuracion, etc..."
          className="w-full h-32 px-4 py-3 border border-light-gray/30 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-none"
        />
      </div>
    </div>
  );
};

const Plataformas: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Platform>('didi');

  const activePlatform = PLATFORMS.find(p => p.id === activeTab)!;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-dark-text font-heading flex items-center">
          <Globe size={28} className="mr-3 text-accent" />
          Plataformas de Delivery
        </h2>
        <p className="text-sm text-medium-gray mt-1">
          Conecta y administra tus plataformas de delivery
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-cream/50 rounded-lg p-1 mb-6 border border-light-gray/20">
        {PLATFORMS.map(platform => (
          <button
            key={platform.id}
            onClick={() => setActiveTab(platform.id)}
            className={`flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
              activeTab === platform.id
                ? 'bg-white text-dark-text shadow-sm'
                : 'text-medium-gray hover:text-dark-text'
            }`}
          >
            <div className={`w-2 h-2 rounded-full ${platform.color} mr-2 ${activeTab === platform.id ? '' : 'opacity-40'}`} />
            {platform.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border border-light-gray/20 p-6">
        <PlatformTab platform={activePlatform} />
      </div>
    </div>
  );
};

export default Plataformas;
